/**
 * Service annuaire des entreprises — recherche-entreprises.api.gouv.fr.
 *
 * API publique gratuite, sans authentification. Mutualise l'appel réseau
 * entre :
 *   - la création de client (`clients-edition.html`, autofill SIRET — EPIC-009) ;
 *   - l'onboarding prospect (`bienvenue.html`, pré-remplissage identité depuis
 *     le SIRET déclaré — EPIC-068).
 *
 * Dégradation gracieuse : aucune de ces fonctions n'est critique. Un échec
 * réseau / rate-limit (429) doit laisser l'utilisateur saisir manuellement —
 * `searchEntreprises()` lève (l'appelant gère), `findEstablishmentBySiret()`
 * renvoie `null` silencieusement.
 */

const SEARCH_URL = "https://recherche-entreprises.api.gouv.fr/search";

/** Ne garde que les chiffres (SIRET/SIREN saisis avec espaces). */
export function onlyDigits(value) {
  return (value || "").replace(/\D/g, "");
}

/**
 * Recherche générique. `query` peut être un SIRET/SIREN (chiffres) ou du texte.
 * `include` liste les champs INSEE additionnels à renvoyer ("siege",
 * "dirigeants"…). Lève en cas de réponse non-OK — l'appelant gère la
 * dégradation gracieuse.
 *
 * @param {string} query
 * @param {{ perPage?: number, include?: string }} [options]
 * @returns {Promise<Array<object>>}
 */
export async function searchEntreprises(query, { perPage = 8, include = "siege" } = {}) {
  const params = new URLSearchParams({
    q: query,
    per_page: String(perPage),
    minimal: "true",
  });
  if (include) params.set("include", include);
  const response = await fetch(`${SEARCH_URL}?${params.toString()}`, {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const data = await response.json();
  return Array.isArray(data?.results) ? data.results : [];
}

/**
 * Résout un établissement à partir d'un SIRET exact (14 chiffres). Renvoie le
 * résultat dont le siège matche le SIRET, sinon le premier résultat. Renvoie
 * `null` si le SIRET n'a pas 14 chiffres ou en cas d'échec réseau (silencieux).
 *
 * @param {string} siret
 * @param {{ include?: string }} [options]
 * @returns {Promise<object|null>}
 */
export async function findEstablishmentBySiret(siret, { include = "siege" } = {}) {
  const digits = onlyDigits(siret);
  if (digits.length !== 14) return null;
  try {
    const results = await searchEntreprises(digits, { include });
    if (results.length === 0) return null;
    return results.find((r) => (r?.siege?.siret || "") === digits) || results[0];
  } catch {
    return null;
  }
}

/**
 * Dérive `{ firstName, lastName }` depuis un résultat annuaire pour une
 * micro-entreprise (personne physique). Préfère le dirigeant structuré
 * (nécessite `include=dirigeants`) ; à défaut, découpe `nom_complet`
 * (« PRÉNOM NOM ») — le premier mot devient le prénom, le reste le nom.
 * Champs toujours destinés à rester éditables par l'utilisateur.
 *
 * @param {object|null} result
 * @returns {{ firstName: string, lastName: string }}
 */
export function derivePersonName(result) {
  const empty = { firstName: "", lastName: "" };
  if (!result) return empty;

  const dirigeants = Array.isArray(result.dirigeants) ? result.dirigeants : [];
  const person = dirigeants.find((d) => d && (d.prenoms || d.nom));
  if (person) {
    const firstName = String(person.prenoms || "").trim().split(/\s+/)[0] || "";
    const lastName = String(person.nom || "").trim();
    if (firstName || lastName) return { firstName, lastName };
  }

  const full = String(result.nom_complet || result.nom_raison_sociale || "").trim();
  if (!full) return empty;
  const parts = full.split(/\s+/);
  if (parts.length === 1) return { firstName: "", lastName: parts[0] };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}
